import React, { useState } from 'react';
import { usePage, router } from '@inertiajs/react';
import { Phone, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import AppShell, { AppShellProps } from '@/components/AppShell';

interface PageProps extends AppShellProps {
  whatsappAccount: {
    id: number;
    business_account_id: string;
    phone_number: string | null;
  } | null;
  flash?: { alert?: string; notice?: string };
}

export default function SetupPhone() {
  const props = usePage<PageProps>().props;
  const { whatsappAccount, flash } = props;
  const [phoneNumber, setPhoneNumber] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number.');
      return;
    }

    setError(null);
    setProcessing(true);

    router.post(
      `/whatsapp_accounts/${whatsappAccount?.id}/save_phone`,
      { phone_number: phoneNumber.trim() },
      {
        onFinish: () => setProcessing(false),
        onError: (errors) => {
          setError(Object.values(errors).join(', ') || 'Verification failed.');
        },
      }
    );
  };

  return (
    <AppShell {...props}>
      <div className="max-w-lg mx-auto py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#25D366] to-[#128C7E] shadow-lg shadow-green-200/50 mb-5">
            <Phone className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#37352F]">Verify Phone Number</h1>
          <p className="text-[#91918E] mt-2 text-sm leading-relaxed max-w-sm mx-auto">
            Enter your WhatsApp Business phone number to complete the setup and start sending messages.
          </p>
        </div>

        {/* Flash messages */}
        {flash?.alert && (
          <div className="flex items-center gap-2 bg-[#FFF3F3] text-[#EB5757] border border-[#FDD8D8] p-3.5 rounded-lg mb-6 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {flash.alert}
          </div>
        )}
        {flash?.notice && (
          <div className="flex items-center gap-2 bg-[#F0FFF4] text-[#276749] border border-[#C6F6D5] p-3.5 rounded-lg mb-6 text-sm">
            <CheckCircle className="h-4 w-4 shrink-0" />
            {flash.notice}
          </div>
        )}

        {/* Form card */}
        <div className="bg-white border border-[#E8E8E5] rounded-xl p-6 shadow-sm">
          <form onSubmit={handleVerify} className="space-y-5">
            <div>
              <label
                htmlFor="phone_number"
                className="block text-sm font-medium text-[#37352F] mb-1.5"
              >
                Phone Number
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                  <Phone className="h-4 w-4 text-[#B4B4B0]" />
                </div>
                <input
                  id="phone_number"
                  type="tel"
                  dir="ltr"
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                    setError(null);
                  }}
                  placeholder="+966 5XX XXX XXXX"
                  className={`block w-full rounded-lg border py-2.5 pl-10 pr-4 text-sm text-[#37352F] placeholder:text-[#B4B4B0] outline-none transition focus:ring-2 focus:ring-offset-1 ${
                    error
                      ? 'border-[#EB5757] focus:ring-[#EB5757]/30'
                      : 'border-[#E8E8E5] focus:border-[#25D366] focus:ring-[#25D366]/30'
                  }`}
                  disabled={processing}
                />
              </div>
              {error && (
                <p className="mt-1.5 text-xs text-[#EB5757] flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {error}
                </p>
              )}
              <p className="mt-1.5 text-xs text-[#B4B4B0]">
                Enter the number with country code (e.g. +966512345678)
              </p>
            </div>

            {/* WABA info */}
            {whatsappAccount?.business_account_id && (
              <div className="bg-[#F7F6F3] rounded-lg px-4 py-3 text-xs text-[#91918E]">
                <span className="font-medium text-[#37352F]">Business Account:</span>{' '}
                {whatsappAccount.business_account_id}
              </div>
            )}

            <button
              type="submit"
              disabled={processing || !phoneNumber.trim()}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#25D366] to-[#128C7E] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Verify & Connect
                </>
              )}
            </button>
          </form>
        </div>

        {/* Help text */}
        <p className="text-center text-xs text-[#B4B4B0] mt-6 leading-relaxed">
          This number must be registered in your{' '}
          <a
            href="https://business.facebook.com/settings/whatsapp-business-accounts"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#91918E] underline hover:text-[#37352F]"
          >
            Meta Business Manager
          </a>
          .
        </p>
      </div>
    </AppShell>
  );
}
